import { useState } from 'react';
import { currentPolicyVersions, type CustomerProfile } from '@emazy/shared-types';

import { useFeedback } from '@/app/feedback';
import { useDocumentMetadata } from '@/hooks/useDocumentMetadata';

import { CustomerProfileApiError } from '../api';
import { AccountPageState } from '../components';
import { useCustomerProfile, useUpdateCommunicationPreferences } from '../hooks';

export function CommunicationPage() {
  useDocumentMetadata('Communication Preferences');
  const query = useCustomerProfile();
  const profile = query.data?.profile;
  return (
    <AccountPageState
      loading={query.isLoading}
      error={query.isError}
      retry={() => void query.refetch()}
    >
      {profile && <CommunicationForm profile={profile} />}
    </AccountPageState>
  );
}

function CommunicationForm({ profile }: { profile: CustomerProfile }) {
  const mutation = useUpdateCommunicationPreferences();
  const feedback = useFeedback();
  const [productUpdatesEmail, setProductUpdatesEmail] = useState(
    profile.communicationPreferences.productUpdatesEmail,
  );
  const [promotionsEmail, setPromotionsEmail] = useState(
    profile.communicationPreferences.promotionsEmail,
  );
  const [conflict, setConflict] = useState(false);
  return (
    <div className="account-page">
      <header>
        <p className="eyebrow">Your inbox, your choice</p>
        <h2>Communication</h2>
        <p>
          Choose optional updates. Essential account, security, and order messages always remain
          enabled.
        </p>
      </header>
      {conflict && (
        <div className="form-alert" role="alert">
          These preferences changed elsewhere. Reload the page before saving again.
        </div>
      )}
      <form
        className="preference-list"
        onSubmit={async (event) => {
          event.preventDefault();
          setConflict(false);
          try {
            await mutation.mutateAsync({
              productUpdatesEmail,
              promotionsEmail,
              expectedVersion: profile.version,
              policyVersion: currentPolicyVersions.marketing,
            });
            feedback.showSuccess(
              'Preferences updated',
              'Your communication choices have been saved.',
            );
          } catch (error) {
            if (error instanceof CustomerProfileApiError && error.code === 'profile-conflict')
              setConflict(true);
          }
        }}
      >
        <label>
          <span>
            <strong>Account and order emails</strong>
            <small>Receipts, security notices, and important service messages.</small>
          </span>
          <input type="checkbox" checked disabled aria-label="Account and order emails required" />
          <em>Required</em>
        </label>
        <label>
          <span>
            <strong>Product updates</strong>
            <small>New personalization tools and product announcements.</small>
          </span>
          <input
            aria-label="Product updates"
            type="checkbox"
            checked={productUpdatesEmail}
            onChange={(event) => setProductUpdatesEmail(event.target.checked)}
          />
        </label>
        <label>
          <span>
            <strong>Promotions and offers</strong>
            <small>Occasional offers and seasonal inspiration.</small>
          </span>
          <input
            aria-label="Promotions and offers"
            type="checkbox"
            checked={promotionsEmail}
            onChange={(event) => setPromotionsEmail(event.target.checked)}
          />
        </label>
        <p className="account-page__timestamp">
          Last updated{' '}
          {new Intl.DateTimeFormat(undefined, {
            dateStyle: 'medium',
            timeStyle: 'short',
          }).format(new Date(profile.updatedAt))}
        </p>
        <button className="button-link button-link--primary" disabled={mutation.isPending}>
          {mutation.isPending ? 'Saving…' : 'Save preferences'}
        </button>
      </form>
    </div>
  );
}
